import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SITE } from "@/lib/api";
import { Download, FileText, CalendarClock, Send, Ban, Euro } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <Reveal>
    <section className="rounded-3xl border border-[#D8E5E8] bg-white p-7 shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] sm:p-10">
      <div className="flex items-center gap-3">
        <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#1F8296]/30 bg-[#1F8296]/10">
          <Icon className="h-5 w-5 text-[#1F8296]" />
        </div>
        <h2 className="font-display text-xl font-semibold text-[#1A2E33] sm:text-2xl">{title}</h2>
      </div>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#5C7178] sm:text-base">{children}</div>
    </section>
  </Reveal>
);

export default function Recesso() {
  return (
    <main>
      <PageHero
        eyebrow="Documento legale"
        titleLines={["Diritto di recesso", "e modulo da scaricare."]}
        subtitle="Informativa sul diritto di recesso ai sensi del D.Lgs. 21 febbraio 2014, n. 21 (Codice del Consumo) per i contratti conclusi a distanza o fuori dai locali commerciali."
      />

      <div className="mx-auto max-w-4xl space-y-6 px-5 py-12 pb-28 sm:px-8" data-testid="recesso-page">
        <Section icon={FileText} title="1. Quando si applica">
          <p>
            Se sei un <strong className="text-[#33484F]">consumatore</strong> (una persona fisica che agisce per
            scopi estranei all'attività imprenditoriale o professionale) e acquisti un nostro corso{" "}
            <strong className="text-[#33484F]">a distanza</strong> (per esempio tramite il sito, per telefono o
            via email) oppure <strong className="text-[#33484F]">fuori dai locali commerciali</strong>, hai
            diritto di recedere dal contratto <strong className="text-[#33484F]">senza alcuna penalità e senza
            dover fornire alcuna motivazione</strong>.
          </p>
        </Section>

        <Section icon={CalendarClock} title="2. Il termine per recedere">
          <p>
            Il periodo di recesso scade dopo <strong className="text-[#33484F]">14 giorni</strong> dalla
            conclusione del contratto. Per rispettare il termine è sufficiente inviare la comunicazione di
            recesso <strong className="text-[#33484F]">prima della scadenza</strong> del periodo.
          </p>
        </Section>

        <Section icon={Send} title="3. Come esercitare il recesso">
          <p>
            Per esercitare il diritto di recesso è sufficiente informarci della tua decisione con una{" "}
            <strong className="text-[#33484F]">dichiarazione inequivocabile</strong>, inviata a:
          </p>
          <p className="rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4 font-semibold text-[#33484F]">
            {SITE.legalName} — {SITE.address}
            <br />
            Email: <a href={`mailto:${SITE.email}`} className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">{SITE.email}</a> —
            Tel: <a href={`tel:${SITE.phone}`} className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">{SITE.phoneDisplay}</a>
          </p>
          <p>
            Puoi usare il <strong className="text-[#33484F]">modulo di recesso tipo</strong> scaricabile da
            questa pagina, ma non è obbligatorio: è sufficiente qualsiasi dichiarazione esplicita. Ti
            consigliamo l'invio con raccomandata A/R o email con conferma, poiché la prova dell'esercizio
            del diritto entro il termine spetta a te.
          </p>
        </Section>

        <Section icon={Euro} title="4. Effetti del recesso">
          <p>
            In caso di recesso ti saranno <strong className="text-[#33484F]">rimborsati tutti i pagamenti</strong>{" "}
            effettuati a nostro favore, senza indebito ritardo e in ogni caso entro{" "}
            <strong className="text-[#33484F]">14 giorni</strong> dal giorno in cui siamo stati informati della
            tua decisione. Il rimborso avviene con lo <strong className="text-[#33484F]">stesso mezzo di
            pagamento</strong> utilizzato per la transazione iniziale, salvo diverso accordo, e senza alcun
            costo a tuo carico.
          </p>
          <p>
            Se hai chiesto espressamente di iniziare la fruizione del servizio durante il periodo di recesso,
            è dovuto un <strong className="text-[#33484F]">importo proporzionale</strong> a quanto già fornito
            fino al momento della comunicazione del recesso.
          </p>
        </Section>

        <Section icon={Ban} title="5. Quando il recesso non si applica (art. 59)">
          <p>
            Il diritto di recesso è escluso, tra gli altri casi, quando il servizio è stato{" "}
            <strong className="text-[#33484F]">pienamente eseguito</strong> con il tuo consenso espresso e con
            l'accettazione della perdita del diritto di recesso a seguito della completa esecuzione.
          </p>
        </Section>

        <Section icon={Download} title="6. Il modulo di recesso tipo">
          <p>
            Qui sotto puoi scaricare il modulo di recesso tipo conforme all'Allegato I, parte B, del
            D.Lgs. 21/2014, già precompilato con i nostri dati. Compilalo e inviacelo per posta o email.
          </p>
          <a
            href="/modulo-recesso.pdf"
            download
            data-testid="recesso-download-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
          >
            <Download className="h-4 w-4" /> Scarica il modulo di recesso (PDF)
          </a>
          <p className="font-plex text-xs uppercase tracking-[0.2em] text-[#7A8F96]">
            Ultimo aggiornamento: ottobre 2026
          </p>
        </Section>
      </div>
    </main>
  );
}
